/**
 * Copyright 2025 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import UserDataStore from './shared/userDataStore';

const urlParams = new URLSearchParams(window.location.search);
const qualities = Object.freeze([
  'uld',
  'vvld',
  'vld',
  'ld',
  'sd',
  'hd',
  'fhd',
  'xhd',
  'uhd'
]);
const storeLocalStateKey = 'WebChannel';
const config = {
  audio: {
    muted: getMustStartMuted(),
    disableAutoMuting: isAutoMutingDisabled()
  },
  enableExperimentalFeatures: areExperimentalFeaturesEnabled(),
  publisher: {
    capabilities: getPublisherCapabilities(),
    quality: getPublisherQuality(),
    token: getPublishToken(),
    decodedToken: decodePublisherToken(),
    channelId: parsePublisherChannelId(),
    storeLocalStateKey
  },
  subscriber: {
    targetLatency: getTargetLatency(),
    token: getStreamToken(),
    decodedToken: decodeSubscriberToken(),
    channelId: parseSubscriberChannelId(),
    chunkedStreaming: {useShaka: useShaka()},
    storeLocalStateKey
  },
  environment: {
    isMobile: isMobile(),
    isSafari: isSafari(),
    isChrome: isChrome(),
    isOpera: isOpera(),
    canPlayHls: canPlayHls()
  },
  qualities
};

function getMustStartMuted() {
  if (isSafari() && !isMobile()) {
    const defaultValue = 'disabled';
    const isAudioStateStored = UserDataStore.get(`${storeLocalStateKey}/audio`, defaultValue);

    return isAudioStateStored === 'disabled';
  }

  return true;
}

function getPublisherQuality() {
  const token = getPublishToken();
  const capabilities = decodeToken(token)?.capabilities || [];
  const publisherQualities = capabilities.filter(capability =>qualities.includes(capability));

  if (publisherQualities.length === 0) {
    return 'sd';
  }

  return publisherQualities[0];
}

function getPublisherCapabilities() {
  const token = getPublishToken();
  const tokenCapabilities = decodeToken(token)?.capabilities || [];

  // Filter out quality-related capabilities and remove duplicates
  return tokenCapabilities.filter((capability, index, array) =>!qualities.includes(capability) && array.indexOf(capability) === index);
}

function getTargetLatency() {
  const targetLatencyParameterValue = getDecodedUrlParameterValue('targetLatency');

  if (!targetLatencyParameterValue) {
    return null;
  }

  return parseFloat(targetLatencyParameterValue);
}

function useShaka() {
  const useShakaParameterValue = getDecodedUrlParameterValue('shaka');

  return useShakaParameterValue === 'true' || useShakaParameterValue === '';
}

function decodeToken(token) {
  decodeToken.cache = decodeToken.cache || {};

  if (decodeToken.cache[token]) {
    return decodeToken.cache[token];
  }

  try {
    if (!token) {
      return;
    }

    if (!token.startsWith('DIGEST:')) {
      console.error('Invalid Token');

      return;
    }

    const decodedTokenString = atob(token.replace('DIGEST:', ''));
    let decodedToken = JSON.parse(decodedTokenString);

    if (decodedToken.token && typeof(decodedToken.token) === 'string') {
      decodedToken = {
        ...decodedToken,
        ...JSON.parse(decodedToken.token)
      };
    }

    decodeToken.cache[token] = decodedToken;

    return decodedToken;
  } catch (e) {
    console.error('Invalid Token', e);

    return;
  }
}

function decodePublisherToken() {
  const token = getPublishToken();

  return decodeToken(token);
}

function decodeSubscriberToken() {
  const token = getStreamToken();

  return decodeToken(token);
}

function parseChannelIdFromToken(token) {
  const roomOrChannelIdRegex = /^(?:room|channel)Id[:](.*)$/;
  const decodedToken = decodeToken(token);
  const idMatch = decodedToken?.requiredTag.match(roomOrChannelIdRegex);

  return idMatch ? idMatch[1] : null;
}

function parsePublisherChannelId() {
  const token = getPublishToken();

  return parseChannelIdFromToken(token);
}

function parseSubscriberChannelId() {
  const token = getStreamToken();

  return parseChannelIdFromToken(token);
}

function isAutoMutingDisabled() {
  return getDecodedUrlParameterValue('disableAutoMute', 'true') === 'true';
}

function getPublishToken() {
  return getDecodedUrlParameterValue('publishToken') || getDecodedUrlParameterValue('token');
}

function getStreamToken() {
  return getDecodedUrlParameterValue('streamToken') || getDecodedUrlParameterValue('edgeToken') || getDecodedUrlParameterValue('token');
}

function areExperimentalFeaturesEnabled() {
  return getDecodedUrlParameterValue('experimental', 'true', 'false') === 'true';
}

function getDecodedUrlParameterValue(parameterName, defaultValue, undefinedValue) {
  if (!urlParams.has(parameterName)) {
    return undefinedValue;
  }

  const value = urlParams.get(parameterName);

  if (value === null) {
    return defaultValue;
  }

  return decodeURIComponent(value);
}

function isMobile() {
  const userAgent = window.navigator.userAgent;

  return isIOS() || isAndroid() || /webOS|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent);
}

function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

function isChrome() {
  return /Chrome|Chromium/i.test(navigator.userAgent);
}

function isOpera() {
  return /Opera|OPR/i.test(navigator.userAgent);
}

function isAndroid() {
  const ua = navigator.userAgent.toLowerCase();

  return ua.includes('android');
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function canPlayHls() {
  return document.createElement('video').canPlayType('application/vnd.apple.mpegURL') === 'maybe';
}

export default config;